import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const BackgroundGlow = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  filter: blur(120px);
  z-index: 0;
  transition: opacity 0.3s var(--transition-smooth);
  
  [data-theme='dark'] & {
    background: radial-gradient(circle at 25% 10%, rgba(255, 77, 77, 0.3), transparent 55%),
                radial-gradient(circle at 75% 0%, rgba(232, 19, 19, 0.25), transparent 60%);
    opacity: 0.75;
  }
  
  [data-theme='light'] & {
    background: radial-gradient(circle at 25% 10%, rgba(255, 77, 77, 0.15), transparent 55%),
                radial-gradient(circle at 75% 0%, rgba(232, 19, 19, 0.12), transparent 60%);
    opacity: 0.5;
  }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh; /* Use dynamic viewport height for mobile */
  width: 100vw;
  max-width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background);
  padding: clamp(var(--spacing-4), 4vw, var(--spacing-8));
  padding-top: max(clamp(var(--spacing-4), 4vw, var(--spacing-8)), env(safe-area-inset-top));
  padding-bottom: max(clamp(var(--spacing-4), 4vw, var(--spacing-8)), env(safe-area-inset-bottom));
  position: relative;
  color: var(--color-text-primary);
  transition: background var(--transition-color-fast), color var(--transition-color-fast);
  box-sizing: border-box;
  overflow-x: hidden;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-4);
    padding-top: max(var(--spacing-4), env(safe-area-inset-top));
    padding-bottom: max(var(--spacing-4), env(safe-area-inset-bottom));
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3);
    padding-top: max(var(--spacing-3), env(safe-area-inset-top));
    padding-bottom: max(var(--spacing-3), env(safe-area-inset-bottom));
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: clamp(var(--radius-lg), 2vw, 1rem);
  padding: clamp(var(--spacing-6), 4vw, var(--spacing-10));
  color: var(--color-text-primary);
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(20px);
  transition: background var(--transition-color-fast), border-color var(--transition-color-fast), color var(--transition-color-fast);
  box-sizing: border-box;
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    max-width: 100%;
    padding: var(--spacing-6);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-5);
    border-radius: var(--radius-md);
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  flex-shrink: 0;
`;

const LogoImage = styled.img`
  width: clamp(12rem, 30vw, 18rem);
  height: auto;
  object-fit: contain;
  background: transparent;
  loading: eager;
`;

const Title = styled.h1`
  font-size: clamp(var(--font-size-xl), 3vw, 1.75rem);
  margin-bottom: 0.5rem;
  text-align: center;
  color: var(--color-text-primary);
  transition: color var(--transition-color-fast);
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: var(--font-size-lg);
  }
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: 2rem;
  transition: color var(--transition-color-fast);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-width: 0; /* Prevent flex shrinking */
`;

const InputBase = styled.input`
  width: 100%;
  padding: clamp(0.75rem, 2vw, 0.85rem) clamp(0.875rem, 2vw, 1rem);
  min-height: 2.75rem; /* 44px - accessibility standard for touch targets */
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface-alt);
  color: var(--color-text-primary);
  font-size: clamp(var(--font-size-sm), 2vw, 1rem);
  transition: background var(--transition-color-fast), border-color var(--transition-color-fast), color var(--transition-color-fast);
  box-sizing: border-box;
  -webkit-appearance: none; /* Remove iOS styling */
  appearance: none;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    background: var(--color-surface-alt) !important;
  }

  &:not(:focus) {
    background: var(--color-surface-alt) !important;
  }

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &[readonly] {
    cursor: text;
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => <InputBase {...props} ref={ref} />
);
Input.displayName = 'Input';

const Button = styled.button`
  margin-top: 0.5rem;
  padding: clamp(0.875rem, 2vw, 0.95rem);
  height: 2.75rem; /* Fixed height - 44px accessibility standard for touch targets */
  border-radius: var(--radius-md);
  border: none;
  background: var(--color-accent);
  color: var(--color-text-on-accent);
  font-weight: 600;
  font-size: clamp(var(--font-size-sm), 2vw, 1rem);
  cursor: pointer;
  transition: background var(--transition-color-fast), font-weight var(--transition-color-fast), box-shadow var(--transition-color-fast), filter var(--transition-color-fast);
  width: 100%;
  min-width: 0; /* Prevent flex shrinking */
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--color-accent-gradient-end);
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98); /* Touch feedback */
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    font-size: 16px; /* Prevent zoom on iOS */
    height: 2.75rem; /* Maintain fixed height on mobile */
  }
`;

const ErrorText = styled.p`
  color: var(--color-error);
  background: var(--color-error-bg);
  border: 1px solid var(--color-border);
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: background var(--transition-color-fast), border-color var(--transition-color-fast), color var(--transition-color-fast);
`;

const SwitchText = styled.p`
  margin-top: 1.25rem;
  color: var(--color-text-secondary);
  text-align: center;
  font-size: 0.95rem;
  transition: color var(--transition-color-fast);
`;

const SwitchButton = styled.button`
  background: none;
  border: none;
  color: var(--color-accent);
  font-weight: 600;
  cursor: pointer;
  margin-left: 0.35rem;
  transition: color var(--transition-color-fast);

  &:hover {
    text-decoration: underline;
    color: var(--color-accent-hover);
  }
`;

interface LoginPageProps {
  onSwitchToRegister: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onSwitchToRegister }) => {
  const { login, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Basic form state management
  React.useEffect(() => {
    setEmail('');
    setPassword('');
    setLocalError(null);
    clearError();
  }, [clearError]);

  // Clear errors when user types
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (localError || error) {
      setLocalError(null);
      clearError();
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (localError || error) {
      setLocalError(null);
      clearError();
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setLocalError(null);
    clearError();
    
    try {
      await login(email, password);
    } catch (err) {
      // Simple error handling - let authService handle the details
      const message = err instanceof Error ? err.message : 'Unable to login. Please try again.';
      setLocalError(message);
      clearError();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <BackgroundGlow />
      <Card>
        <LogoSection>
          <LogoImage 
            src="/logo_and_name_red.png" 
            alt="Vermilion Vision - Coach Assistant" 
            width="512"
            height="128"
          />
        </LogoSection>
        <Title>Welcome</Title>
        <Subtitle>Sign in to access your coaching assistant</Subtitle>

        <Form onSubmit={handleSubmit} aria-label="Login form" noValidate>
          <Input
            type="email"
            name="email"
            id="email-input"
            placeholder="Email address"
            value={email}
            onChange={handleEmailChange}
            required
            autoComplete="email"
            aria-label="Email address"
            aria-required="true"
            aria-invalid={localError ? "true" : "false"}
            aria-describedby={localError ? "email-error" : undefined}
          />
          <Input
            type="password"
            name="password"
            id="password-input"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            required
            autoComplete="current-password"
            aria-label="Password"
            aria-required="true"
            aria-invalid={localError ? "true" : "false"}
            aria-describedby={localError ? "password-error" : undefined}
          />

          {localError && (
            <ErrorText 
              id="email-error" 
              role="alert" 
              aria-live="polite"
            >
              {localError}
            </ErrorText>
          )}

          <Button 
            type="submit" 
            disabled={submitting}
            aria-label={submitting ? 'Signing in, please wait' : 'Sign in to your account'}
            aria-busy={submitting}
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>

        <SwitchText>
          Need an account?{' '}
          <SwitchButton 
            type="button" 
            onClick={onSwitchToRegister}
            aria-label="Create a new account"
          >
            Create one
          </SwitchButton>
        </SwitchText>
      </Card>
    </PageContainer>
  );
};




